
import React, { useState, useRef } from 'react';
import { Form, Button, Row, Col, Table, Card } from 'react-bootstrap';
import html2pdf from 'html2pdf.js';

interface InvoiceItem {
  description: string;
  quantity: number;
  price: number;
}

const InvoiceGenerator: React.FC = () => {
  const [clientName, setClientName] = useState<string>('');
  const [clientAddress, setClientAddress] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [invoiceDate, setInvoiceDate] = useState<string>('');
  const [items, setItems] = useState<InvoiceItem[]>([
    { description: '', quantity: 1, price: 0 },
  ]);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('default');

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: string | number) => {
    const newItems = [...items];
    (newItems[index][field] as any) = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, price: 0 }]);
  };

  const removeItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + item.quantity * item.price, 0);
  };

  const invoiceRef = useRef<HTMLDivElement>(null);

  const generateInvoice = () => {
    if (invoiceRef.current) {
      html2pdf().from(invoiceRef.current).save();
    }
  };

  return (
    <Card className={`p-4 invoice-template-${selectedTemplate}`} ref={invoiceRef}>
      {companyLogo && (
        <div className="text-center mb-4">
          <img src={companyLogo} alt="Company Logo" style={{ maxWidth: '200px', maxHeight: '100px' }} />
        </div>
      )}
      <h3 className="mb-4 text-center">Invoice Generator</h3>
      <Form>
        <Form.Group controlId="companyLogo" className="mb-3">
          <Form.Label>Company Logo</Form.Label>
          <Form.Control
            type="file"
            accept="image/*"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (e.target.files && e.target.files[0]) {
                const reader = new FileReader();
                reader.onload = (event) => {
                  setCompanyLogo(event.target?.result as string);
                };
                reader.readAsDataURL(e.target.files[0]);
              }
            }}
          />
          {companyLogo && (
            <div className="mt-2">
              <img src={companyLogo} alt="Company Logo" style={{ maxWidth: '150px', maxHeight: '150px' }} />
            </div>
          )}
        </Form.Group>

        <Form.Group controlId="invoiceTemplate" className="mb-3">
          <Form.Label>Invoice Template</Form.Label>
          <Form.Select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)}>
            <option value="default">Default Template</option>
            <option value="modern">Modern Template</option>
          </Form.Select>
        </Form.Group>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="invoiceNumber">
              <Form.Label>Invoice Number</Form.Label>
              <Form.Control
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="INV-001"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="invoiceDate">
              <Form.Label>Invoice Date</Form.Label>
              <Form.Control
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <h4 className="mt-4 mb-3">Client Details</h4>
        <Form.Group className="mb-3" controlId="clientName">
          <Form.Label>Client Name</Form.Label>
          <Form.Control
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="Enter client name"
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="clientAddress">
          <Form.Label>Client Address</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={clientAddress}
            onChange={(e) => setClientAddress(e.target.value)}
            placeholder="Enter client address"
          />
        </Form.Group>

        <h4 className="mt-4 mb-3">Items</h4>
        <Table striped bordered hover className="mb-3">
          <thead>
            <tr>
              <th>Description</th>
              <th>Quantity</th>
              <th>Price</th>
              <th>Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index}>
                <td>
                  <Form.Control
                    type="text"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                  />
                </td>
                <td>
                  <Form.Control
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', Number(e.target.value))}
                  />
                </td>
                <td>
                  <Form.Control
                    type="number"
                    value={item.price}
                    onChange={(e) => handleItemChange(index, 'price', Number(e.target.value))}
                  />
                </td>
                <td>{(item.quantity * item.price).toFixed(2)}</td>
                <td>
                  <Button variant="danger" size="sm" onClick={() => removeItem(index)}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Button variant="secondary" onClick={addItem} className="mb-4">
          Add Item
        </Button>

        <h4 className="text-end">Total: {calculateTotal().toFixed(2)}</h4>

        <div className="text-center mt-4">
          <Button variant="primary" size="lg" onClick={generateInvoice}>
            Generate Invoice
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default InvoiceGenerator;
